import { FC, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckedIcon from "@mui/icons-material/CheckCircle";
import UncheckedIcon from "@mui/icons-material/HighlightOff";
import { Button, Grid, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormReadOnlyField from "../../../components/forms/FormReadOnlyField";
import FormSectionTitle from "../../../components/forms/FormSectionTitle";
import FormSelect from "../../../components/forms/FormSelect";
import FormTextField from "../../../components/forms/FormTextField";
import { ApiRoutes } from "../../../datasources/networking/apiRoutes";
import { UpdateCurrentAccountRegistryPayload, useAuthValidationSchemas } from "../../../datasources/requests/auth";
import { UserAccountResponse } from "../../../datasources/responses/auth";
import { useAuthentication } from "../../../hooks/useAuthentication";
import { useStaticOptions } from "../../../hooks/useOptions";
import { usePreferences } from "../../../hooks/usePreferences";
import { useResource } from "../../../hooks/useResource";

interface Props {
	updateAccountDisplayName?: (value?: string) => void;
}

const cellSize = { xs: 12, sm: 12, md: 4, lg: 4, xl: 4 };

/**
 * CurrentUserRegistry renders the tab containing current user details.
 *
 * @param updateAccountDisplayName
 * @constructor
 */
const CurrentUserRegistry: FC<Props> = ({ updateAccountDisplayName }) => {
	const { languages } = useStaticOptions();
	const { selectLanguage } = usePreferences();
	const { t } = useTranslation("feature.account");
	const { updateLocalUserDisplayData } = useAuthentication();

	const {
		data: account,
		isLoading,
		put,
	} = useResource<UpdateCurrentAccountRegistryPayload, UserAccountResponse>(
		{ route: ApiRoutes.AuthMe },
		{},
		{ subjectKey: "account" }
	);

	const { UpdateCurrentAccountSchema } = useAuthValidationSchemas();
	const form = useForm<UpdateCurrentAccountRegistryPayload>({
		resolver: zodResolver(UpdateCurrentAccountSchema),
	});

	const handleUpdateAccount = form.handleSubmit((payload: UpdateCurrentAccountRegistryPayload) => {
		put(payload, {
			onSuccess: (response) => {
				selectLanguage(response?.preferredLanguage);
				updateLocalUserDisplayData(response!.firstName, response!.lastName, response!.email);
			},
		});
	});

	useEffect(() => {
		form.reset({
			firstName: account?.firstName ?? "",
			lastName: account?.lastName ?? "",
			telephone: account?.telephone ?? "",
			preferredLanguage: account?.preferredLanguage ?? "",
		});

		updateAccountDisplayName?.(account ? [account.firstName, account.lastName].join(" ") : undefined);
	}, [account, form, updateAccountDisplayName]);

	return (
		<>
			<Grid container spacing={2}>
				<Grid size={cellSize}>
					<FormTextField
						form={form}
						label={t("first name")}
						name="firstName"
						rules={{ required: true }}
						fullWidth
						isLoading={isLoading}
					/>
				</Grid>

				<Grid size={cellSize}>
					<FormTextField
						form={form}
						label={t("last name")}
						rules={{ required: true }}
						name="lastName"
						fullWidth
						isLoading={isLoading}
					/>
				</Grid>
				<Grid size={cellSize}>
					<FormTextField
						form={form}
						label={t("telephone")}
						rules={{ required: true }}
						name="telephone"
						fullWidth
						isLoading={isLoading}
					/>
				</Grid>
			</Grid>

			<FormSectionTitle title={t("account (read-only)")} />
			<Grid container spacing={2}>
				<Grid size={cellSize}>
					<FormReadOnlyField
						label={t("role description")}
						value={account?.roleDescription ?? ""}
						fullWidth
						isLoading={isLoading}
					/>
				</Grid>
				<Grid size={cellSize}>
					<FormReadOnlyField
						label={t("email")}
						value={account?.email ?? ""}
						fullWidth
						isLoading={isLoading}
					/>
				</Grid>

				<Grid size={cellSize}>
					<FormReadOnlyField
						label={t("username")}
						value={account?.username ?? ""}
						fullWidth
						isLoading={isLoading}
					/>
				</Grid>
			</Grid>

			<FormSectionTitle title={t("preferences")} />
			<Grid container spacing={2} alignItems="center">
				<Grid size={cellSize}>
					<FormSelect
						form={form}
						label={t("preferred language")}
						name="preferredLanguage"
						fullWidth
						isLoading={isLoading}
						options={languages}
					/>
				</Grid>

				<Grid size={12}>
					<Stack justifyContent={"space-between"} direction="row">
						<Stack direction="row" spacing={2} alignItems={"center"}>
							{account && !isLoading && <Typography variant="caption">{t("tos accepted")}</Typography>}
							{!isLoading && account?.tosAccepted && <CheckedIcon fontSize="small" color="primary" />}
							{!isLoading && !account?.tosAccepted && <UncheckedIcon fontSize="small" color="error" />}
						</Stack>

						<Button
							loading={isLoading}
							className="confirm"
							loadingPosition="start"
							onClick={handleUpdateAccount}
							disabled={!form.formState.isValid}
						>
							{t("apply", { ns: "common" })}
						</Button>
					</Stack>
				</Grid>
			</Grid>
		</>
	);
};

export default CurrentUserRegistry;
